import { memo, type ReactNode } from 'react';

import { cn } from '@air/utils-tailwind';

import { type FileStatus } from '../../constants/FileStatus';
import { FileStatusIcon } from './FileStatusIcon';
import { FileStatusPanelHeaderButtons, type FileStatusPanelHeaderButtonsProps } from './FileStatusPanelHeaderButtons';
import { FileStatusPanelItem } from './FileStatusPanelItem';
import { FileStatusPanelSummary } from './FileStatusPanelSummary';
import { FileStatusSubtitle } from './FileStatusSubtitle';
import { FileStatusTitle } from './FileStatusTitle';

export type FileStatusPanelHeaderProps = FileStatusPanelHeaderButtonsProps & {
  title: ReactNode;
  subtitle: ReactNode;
  disableCancel?: boolean;
  disableDismiss?: boolean;
  status: FileStatus;
  className?: string;
  summary?: ReactNode;
  avatar?: ReactNode;
};

export const FileStatusPanelHeader = memo(
  ({
    isUploaderOpen,
    onToggle,
    status,
    title,
    subtitle,
    disableCancel,
    disableDismiss,
    onCancel,
    onDismiss,
    onPause,
    onResume,
    className,
    summary,
    avatar,
  }: FileStatusPanelHeaderProps) => {
    return (
      <div>
        <FileStatusPanelItem
          avatar={avatar ?? <FileStatusIcon status={status} />}
          buttons={
            <FileStatusPanelHeaderButtons
              disableCancel={disableCancel}
              disableDismiss={disableDismiss}
              isUploaderOpen={isUploaderOpen}
              onCancel={onCancel}
              onDismiss={onDismiss}
              onPause={onPause}
              onResume={onResume}
              onToggle={onToggle}
              status={status}
            />
          }
          className={cn('px-4 py-3', isUploaderOpen ? 'rounded-none' : 'rounded-4', className)}
          subtitle={
            <FileStatusSubtitle className="truncate" data-testid="UPLOADER_SUBTITLE">
              {subtitle}
            </FileStatusSubtitle>
          }
          title={
            <FileStatusTitle className="font-semibold" data-testid="UPLOADER_TITLE">
              {title}
            </FileStatusTitle>
          }
        />
        {summary && <FileStatusPanelSummary>{summary}</FileStatusPanelSummary>}
      </div>
    );
  },
);

FileStatusPanelHeader.displayName = 'FileStatusPanelHeader';
